import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Receipt, Plus, Eye } from 'lucide-react'
import { formatDate, formatCurrency } from '@/lib/utils'
import type { Facture } from '@/types/database-real'

interface ClientInvoicesTabProps {
  invoices: Facture[]
  clientId: string
}

const statusVariants: Record<string, 'mint' | 'gold' | 'brown' | 'red' | 'default'> = {
  draft: 'default',
  sent: 'gold',
  paid: 'mint',
  partial: 'brown',
  overdue: 'red',
  cancelled: 'red',
}

const statusLabels: Record<string, string> = {
  draft: 'Brouillon',
  sent: 'Envoyée',
  paid: 'Payée',
  partial: 'Partiel',
  overdue: 'En retard',
  cancelled: 'Annulée',
}

export function ClientInvoicesTab({ invoices, clientId }: ClientInvoicesTabProps) {
  if (invoices.length === 0) {
    return (
      <EmptyState
        icon={<Receipt className="w-12 h-12" />}
        title="Aucune facture"
        description="Ce client n'a pas encore de facture."
        action={
          <Link to={`/factures/new?client=${clientId}`}>
            <Button leftIcon={<Plus className="w-4 h-4" />}>
              Créer une facture
            </Button>
          </Link>
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Link to={`/factures/new?client=${clientId}`}>
          <Button variant="outline" leftIcon={<Plus className="w-4 h-4" />}>
            Nouvelle facture
          </Button>
        </Link>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-beige-50 border-b border-beige-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Numéro
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Statut
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Montant HT
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Montant TTC
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Créé le
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Échéance
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-beige-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-beige-50">
                  <td className="px-4 py-3 font-medium text-brown-900">
                    {invoice.numero || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariants[invoice.statut] || 'default'}>
                      {statusLabels[invoice.statut] || invoice.statut}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-brown-600">
                    {invoice.total_ht ? formatCurrency(invoice.total_ht) : '-'}
                  </td>
                  <td className="px-4 py-3 font-medium text-brown-900">
                    {invoice.total_ttc ? formatCurrency(invoice.total_ttc) : formatCurrency(invoice.montant || 0)}
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-500">
                    {invoice.created_at ? formatDate(invoice.created_at) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-500">
                    {invoice.echeance ? formatDate(invoice.echeance) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/factures/${invoice.id}`}>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
